import React, { useState } from 'react';
import { X, CheckCircle, AlertCircle, ExternalLink, Info } from 'lucide-react';
import { LinkedInIntegrationFactory } from '../../services/integrations/social/LinkedInIntegrationFactory';
import { SelectField } from "components/ui/select-field";

interface LinkedInIntegrationSetupProps {
  onIntegrationCreated: (integration: any) => void;
  onCancel: () => void;
}

export const LinkedInIntegrationSetup: React.FC<LinkedInIntegrationSetupProps> = ({
  onIntegrationCreated,
  onCancel
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState<any>(null);

  // Form data
  const [formData, setFormData] = useState({
    name: 'LinkedIn Marketing',
    accessToken: '',
    clientId: '',
    clientSecret: '',
    selectedOrganizations: [] as string[],
    enableOrganicPosting: true,
    enableAnalytics: true,
    timezone: 'UTC',
    businessHours: {
      start: '09:00',
      end: '17:00'
    }
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleNestedInputChange = (parent: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...(prev[parent as keyof typeof prev] as any),
        [field]: value
      }
    }));
    setError('');
  };

  const handleTestCredentials = async () => {
    if (!formData.accessToken || !formData.clientId || !formData.clientSecret) {
      setError('Please fill in all required credential fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await LinkedInIntegrationFactory.testCredentials({
        accessToken: formData.accessToken,
        clientId: formData.clientId,
        clientSecret: formData.clientSecret
      });

      setTestResult(result);

      if (result.success) {
        // If organizations are available, populate them
        if (result.availableOrganizations && result.availableOrganizations.length > 0) {
          setFormData(prev => ({
            ...prev,
            selectedOrganizations: [result.availableOrganizations[0].id]
          }));
        }
        setStep(2);
      } else {
        setError(result.message || 'Failed to validate credentials');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to test credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIntegration = async () => {
    setLoading(true);
    setError('');

    try {
      const integration = await LinkedInIntegrationFactory.createFromWizard(formData);
      onIntegrationCreated(integration);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to create integration');
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-content mb-2">LinkedIn API Credentials</h3>
        <p className="text-content-secondary mb-4">
          Enter your LinkedIn application credentials to connect your LinkedIn Marketing account.
        </p>
        
        <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg mb-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-300 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 dark:text-blue-300">Need LinkedIn API credentials?</h4>
              <p className="text-blue-800 dark:text-blue-300 text-sm mt-1">
                Create a LinkedIn application in the LinkedIn Developer Portal to get your credentials.
              </p>
              <a
                href="https://developer.linkedin.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-300 text-sm mt-2"
              >
                LinkedIn Developer Portal
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Integration Name
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="LinkedIn Marketing"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Access Token *
          </label>
          <input
            type="password"
            value={formData.accessToken}
            onChange={(e) => handleInputChange('accessToken', e.target.value)}
            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter your LinkedIn access token"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Client ID *
          </label>
          <input
            type="text"
            value={formData.clientId}
            onChange={(e) => handleInputChange('clientId', e.target.value)}
            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter your LinkedIn application client ID"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Client Secret *
          </label>
          <input
            type="password"
            value={formData.clientSecret}
            onChange={(e) => handleInputChange('clientSecret', e.target.value)}
            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter your LinkedIn application client secret"
          />
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-300" />
          <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-content mb-2">Configuration</h3>
        <p className="text-content-secondary mb-4">
          Configure your LinkedIn integration settings and preferences.
        </p>
      </div>

      {testResult?.userProfile && (
        <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-300" />
            <span className="font-medium text-green-900 dark:text-green-300">Connected Successfully</span>
          </div>
          <p className="text-green-800 dark:text-green-300 text-sm">
            Connected as: {testResult.userProfile.name}
          </p>
          {testResult.availableOrganizations && testResult.availableOrganizations.length > 0 && (
            <p className="text-green-800 dark:text-green-300 text-sm">
              Found {testResult.availableOrganizations.length} organization(s)
            </p>
          )}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <h4 className="font-medium text-content mb-3">Features</h4>
          <div className="space-y-3">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.enableOrganicPosting}
                onChange={(e) => handleInputChange('enableOrganicPosting', e.target.checked)}
                className="w-4 h-4 text-blue-600 dark:text-blue-300 border-line-strong rounded focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-content">Organic Posting</span>
                <p className="text-sm text-content-secondary">Enable posting content to LinkedIn</p>
              </div>
            </label>

            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.enableAnalytics}
                onChange={(e) => handleInputChange('enableAnalytics', e.target.checked)}
                className="w-4 h-4 text-blue-600 dark:text-blue-300 border-line-strong rounded focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-content">Analytics & Engagement Tracking</span>
                <p className="text-sm text-content-secondary">Track post performance and engagement metrics</p>
              </div>
            </label>
          </div>
        </div>

        <div>
          <h4 className="font-medium text-content mb-3">Posting Schedule</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Timezone
              </label>
              <SelectField
                value={formData.timezone}
                onChange={(e) => handleInputChange('timezone', e.target.value)}
                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
                <option value="Europe/London">London</option>
                <option value="Europe/Paris">Paris</option>
                <option value="Asia/Tokyo">Tokyo</option>
              </SelectField>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Business Hours Start
              </label>
              <input
                type="time"
                value={formData.businessHours.start}
                onChange={(e) => handleNestedInputChange('businessHours', 'start', e.target.value)}
                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Business Hours End
              </label>
              <input
                type="time"
                value={formData.businessHours.end}
                onChange={(e) => handleNestedInputChange('businessHours', 'end', e.target.value)}
                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-300" />
          <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-content">LinkedIn Integration Setup</h2>
          <p className="text-content-secondary">Connect your LinkedIn Marketing account</p>
        </div>
        <button aria-label="Close"
          onClick={onCancel}
          className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center mb-8">
        <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
          step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-dark-600 text-content-secondary'
        }`}>
          1
        </div>
        <div className={`flex-1 h-1 mx-4 ${
          step >= 2 ? 'bg-blue-600' : 'bg-gray-200 dark:bg-dark-600'
        }`} />
        <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
          step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-dark-600 text-content-secondary'
        }`}>
          2
        </div>
      </div>

      {/* Step Content */}
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}

      {/* Actions */}
      <div className="flex items-center justify-between pt-6 border-t">
        <div className="text-sm text-content-secondary">
          {step === 1 && 'Step 1 of 2: Credentials'}
          {step === 2 && 'Step 2 of 2: Configuration'}
        </div>
        
        <div className="flex items-center gap-3">
          {step === 2 && (
            <button
              onClick={() => setStep(1)}
              className="px-4 py-2 border border-line-strong text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
              disabled={loading}
            >
              Back
            </button>
          )}
          
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-line-strong text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
            disabled={loading}
          >
            Cancel
          </button>
          
          {step === 1 && (
            <button
              onClick={handleTestCredentials}
              disabled={loading || !formData.accessToken || !formData.clientId || !formData.clientSecret}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? 'Testing...' : 'Test & Continue'}
            </button>
          )}
          
          {step === 2 && (
            <button
              onClick={handleCreateIntegration}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? 'Creating...' : 'Create Integration'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};